/**
 * Die SVG-Symbole der Navigation und der Kopfzeile — 1:1 aus dem Entwurf
 * „IWM Hub Redesign" (Zeilen 76–174) übernommen (REQ-20 §3.2).
 *
 * Alle Symbole zeichnen auf einer 20×20-Fläche und färben sich über
 * `currentColor`; die Größe gibt der Aufrufer an (Navigation 17px, Kopfzeile
 * 15px). Sie sind rein dekorativ — die Beschriftung steht als Text daneben —
 * und deshalb `aria-hidden`.
 *
 * **Export-Vertrag** (auch vom Dashboard-Paket benutzt, deshalb stabil halten):
 *
 * ```ts
 * navIconFor(entity: EntityTypes, props?: NavIconProps): ReactElement
 * defaultNavIcon(props?: NavIconProps): ReactElement
 * ```
 */

import type { ReactElement, ReactNode } from 'react';

import { EntityTypes } from '@/types/enums';

export interface NavIconProps {
  /** Kantenlänge in Pixeln; Standard 17 wie im Entwurf. */
  size?: number;
}

/** Symbol zu einem `EntityTypes`-Wert. */
export type NavIconRenderer = (props?: NavIconProps) => ReactElement;

const DEFAULT_SIZE = 17;

function icon(props: NavIconProps | undefined, children: ReactNode): ReactElement {
  const size = props?.size ?? DEFAULT_SIZE;
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
    >
      {children}
    </svg>
  );
}

/** Listensymbol (Entwurf Zeile 110) — Standard für Entitäten ohne eigenes Symbol. */
export function defaultNavIcon(props?: NavIconProps): ReactElement {
  return icon(
    props,
    <path
      d="M4 5h12M4 10h12M4 15h6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />,
  );
}

/** Tabellensymbol (Entwurf Zeile 87) — Bestellposition. */
function tableIcon(props?: NavIconProps): ReactElement {
  return icon(
    props,
    <>
      <rect x="2.5" y="3.5" width="15" height="13" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M2.5 8h15M7.5 8v8.5" stroke="currentColor" strokeWidth="1.5" />
    </>,
  );
}

/** Würfelsymbol (Entwurf Zeile 92) — Container. */
function cubeIcon(props?: NavIconProps): ReactElement {
  return icon(
    props,
    <>
      <path
        d="M10 2.6 17 6v8l-7 3.4L3 14V6l7-3.4Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M3 6l7 3.3L17 6M10 9.3v8.1" stroke="currentColor" strokeWidth="1.5" />
    </>,
  );
}

/** Belegsymbol (Entwurf Zeile 97) — Rechnung. */
function receiptIcon(props?: NavIconProps): ReactElement {
  return icon(
    props,
    <>
      <rect x="4" y="2.5" width="12" height="15" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M7 7h6M7 10.2h6M7 13.4h3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </>,
  );
}

/** Häkchen im Kasten (Entwurf Zeile 130) — Rechnungszuordnung. */
function checkBoxIcon(props?: NavIconProps): ReactElement {
  return icon(
    props,
    <>
      <path
        d="M6 9.5 9 12.5l5.5-6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect
        x="2.8"
        y="2.8"
        width="14.4"
        height="14.4"
        rx="3"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </>,
  );
}

/**
 * Zuordnung Entität → Symbol. Nur die vier Entitäten mit eigener Seite stehen
 * hier; alle übrigen liefert `defaultNavIcon`.
 */
const ENTITY_ICONS: Partial<Record<EntityTypes, NavIconRenderer>> = {
  [EntityTypes.Bestellposition]: tableIcon,
  [EntityTypes.Container]: cubeIcon,
  [EntityTypes.Rechnung]: receiptIcon,
  [EntityTypes.Rechnungszuordnung]: checkBoxIcon,
};

/** Symbol zu einer Entität; unbekannte Entitäten erhalten `defaultNavIcon`. */
export function navIconFor(entity: EntityTypes, props?: NavIconProps): ReactElement {
  return (ENTITY_ICONS[entity] ?? defaultNavIcon)(props);
}

// -----------------------------------------------------------------------------
// Symbole der Schale selbst
// -----------------------------------------------------------------------------

/** Kachelsymbol des Dashboard-Eintrags (Entwurf Zeile 76). */
export function dashboardNavIcon(props?: NavIconProps): ReactElement {
  return icon(
    props,
    <>
      <rect x="2.5" y="2.5" width="6" height="6" rx="1.5" fill="currentColor" />
      <rect x="11.5" y="2.5" width="6" height="6" rx="1.5" fill="currentColor" opacity=".55" />
      <rect x="2.5" y="11.5" width="6" height="6" rx="1.5" fill="currentColor" opacity=".55" />
      <rect x="11.5" y="11.5" width="6" height="6" rx="1.5" fill="currentColor" />
    </>,
  );
}

/** Zahnrad der Administration (Entwurf Zeile 137). */
export function adminNavIcon(props?: NavIconProps): ReactElement {
  return icon(
    props,
    <>
      <circle cx="10" cy="10" r="2.6" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M10 2.6v2M10 15.4v2M17.4 10h-2M4.6 10h-2M15.2 4.8l-1.4 1.4M6.2 13.8l-1.4 1.4M15.2 15.2l-1.4-1.4M6.2 6.2 4.8 4.8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </>,
  );
}

/** Doppelpfeil des Ein- und Ausklappens (Entwurf Zeile 141). */
export function collapseNavIcon(props?: NavIconProps): ReactElement {
  return icon(
    props,
    <path
      d="M11 5.5 6.5 10 11 14.5M15.5 5.5 11 10l4.5 4.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />,
  );
}

/**
 * Pfeil der Gruppentitel. Der Entwurf zeigt statische Gruppentitel und hat
 * dafür kein Symbol; der Pfeil folgt dem Strichbild der übrigen Symbole und
 * dreht sich beim Aufklappen um 90° (REQ-20 §3.1).
 */
export function groupChevronIcon(props?: NavIconProps): ReactElement {
  return icon(
    props,
    <path
      d="M8 6.5 11.5 10 8 13.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />,
  );
}

/** Sonne des Hell/Dunkel-Umschalters (Entwurf Zeile 168). */
export function lightThemeIcon(props?: NavIconProps): ReactElement {
  return icon(
    props,
    <>
      <circle cx="10" cy="10" r="3.4" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M10 2.8v1.8M10 15.4v1.8M17.2 10h-1.8M4.6 10H2.8M15.1 4.9l-1.3 1.3M6.2 13.8l-1.3 1.3M15.1 15.1l-1.3-1.3M6.2 6.2 4.9 4.9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </>,
  );
}

/** Mond des Hell/Dunkel-Umschalters (Entwurf Zeile 171). */
export function darkThemeIcon(props?: NavIconProps): ReactElement {
  return icon(
    props,
    <path
      d="M16 12.4A6.8 6.8 0 0 1 7.6 4a6.9 6.9 0 1 0 8.4 8.4Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />,
  );
}
